import React, { useState, useContext, useMemo, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Person, Lock } from "@mui/icons-material";
import { DataContext } from "../context/DataContext";
import CreditsEngineService from "../services/CreditsEngineService";
import PlanComparisonModal from "./PlanComparisonModal";
import UserProfileService from "../services/UserProfileService";
import TotpEnrollmentForm from "./security/TotpEnrollmentForm";
import ChangePasswordForm from "./security/ChangePasswordForm";

// Importamos las funciones de tu carpeta api
import SendPhoneVerificationCode from "../api/SendPhoneVerificationCode";
import VerifyPhoneCode from "../api/VerifyPhoneCode";

const ProfileDrawer = ({ open, onClose, user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const headerHeight = isMobile ? 56 : 64;
  const { accountId, isPlatformRoot, isTenantAdmin, userData, myRole } = useContext(DataContext);
  const resolvedAccountId = useMemo(() => {
    if (accountId) return accountId;
    if (user?.accountId) return user.accountId;
    if (user?.account_id) return user.account_id;
    return null;
  }, [accountId, user]);

  // Rol/email/teléfono reales -- ver nota más abajo sobre por qué no se usa
  // el `user` prop para esto (viene mayormente estático de HeaderWithDropdown.js).
  // role_name del catálogo hoy es en inglés ("Admin"/"Analyst"/"Viewer"), se
  // traduce para mostrar. isPlatformRoot tiene prioridad (root no tiene fila
  // de rol propia, ver _compute_permissions en whisperbi-back-security).
  const ROLE_NAME_ES = { Admin: "Administrador", Analyst: "Analista", Viewer: "Visualizador" };
  const roleLabel = isPlatformRoot
    ? "Administrador de plataforma"
    : myRole?.role_name
      ? (ROLE_NAME_ES[myRole.role_name] || myRole.role_name)
      : isTenantAdmin
        ? "Administrador"
        : "Miembro";
  const resolvedEmail = userData?.attributes?.email || user?.email || "";
  const resolvedPhone = userData?.attributes?.phone_number || "";

  // estados locales
  const [codigo, setCodigo] = useState("");
  const [telefonoVerificado, setTelefonoVerificado] = useState(
    user?.phone_verified || false
  );

  // Plan actual (ver también UpgradePlanBanner.js en el chat)
  const [planInfo, setPlanInfo] = useState(null);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  useEffect(() => {
    if (!resolvedAccountId) return;
    CreditsEngineService.getLimits({ accountId: resolvedAccountId })
      .then((response) => setPlanInfo(response?.account_policy || null))
      .catch((err) => console.error('No se pudo obtener la información del plan actual', err));
  }, [resolvedAccountId]);

  // Estado real de MFA -- el `user` prop de este componente hoy trae datos
  // mayormente estáticos (ver HeaderWithDropdown.js), así que se resuelve
  // directo contra el perfil real (GET /useraccount/{user_id}, que ya
  // incluye security.mfa_enabled -- ver user-account/app.py::get_user_account).
  // Mismo fetch también alimenta `registered` (audit.created_at) más abajo,
  // por eso ahora se guarda el perfil completo, no solo el booleano de MFA.
  const [mfaEnabled, setMfaEnabled] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setMfaLoading(true);
    UserProfileService.getProfile()
      .then((profile) => {
        setMfaEnabled(Boolean(profile?.security?.mfa_enabled));
        setProfileData(profile || null);
      })
      .catch((err) => console.error("No se pudo obtener el estado de MFA", err))
      .finally(() => setMfaLoading(false));
  }, [open]);

  const registeredLabel = useMemo(() => {
    const createdAt = profileData?.audit?.created_at;
    if (!createdAt) return null;
    const parsed = new Date(createdAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString("es-MX");
  }, [profileData]);

  // estilo reutilizable para cada tarjeta/sección
  const cardStyle = {
    backgroundColor: "var(--wb-x-bg-1c1f26, #1c1f26)",
    borderRadius: 3,
    p: 3,
    mb: 3,
    border: "1px solid var(--wb-overlay-10)",
  };

  // --- Funciones de verificación ---
  const handleSendCode = async () => {
    const res = await SendPhoneVerificationCode();
    alert(res.message);
  };

  const handleVerifyCode = async () => {
    const res = await VerifyPhoneCode(codigo);
    if (res.success) {
      setTelefonoVerificado(true);
    }
    alert(res.message);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: isMobile ? "100%" : 500,
          backgroundColor: "var(--wb-bg-deep)",
          color: "var(--wb-text-strong)",
          padding: 3,
          marginTop: `${headerHeight}px`,
          height: `calc(100% - ${headerHeight}px)`,
          overflowY: "auto",
        },
      }}
    >
      {/* Título principal */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Mi Perfil
        </Typography>
        <Typography variant="body2" color="gray">
          Administra tu información personal y configuración de cuenta
        </Typography>
      </Box>

      {/* Sección: Foto de perfil */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "var(--wb-x-bg-1976d2, #1976d2)",
              fontSize: "1.5rem",
              mr: 2,
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6">{user?.name}</Typography>
            <Typography variant="body2" color="gray">
              {roleLabel}
              {registeredLabel ? ` — Registrado: ${registeredLabel}` : ""}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Editar Perfil
        </Button>
      </Box>

      {/* Sección: Información Personal */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Person sx={{ color: "gray", mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Información Personal
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            Nombre Completo
          </Typography>
          <Typography variant="body1">{user?.name}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            Rol
          </Typography>
          <Typography variant="body1">{roleLabel}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            Correo Electrónico
          </Typography>
          <Typography variant="body1">{resolvedEmail || "No disponible"}</Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            ID de cuenta
          </Typography>
          <Typography variant="body1">
            {resolvedAccountId || "No disponible"}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            Privilegios
          </Typography>
          <Typography variant="body1">
            {isPlatformRoot
              ? "Root / Administrador de plataforma"
              : isTenantAdmin
                ? "Administrador de cuenta"
                : "Usuario estándar"}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            Plan actual
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body1">{planInfo?.plan_name || "Free"}</Typography>
            <Button
              size="small"
              onClick={() => setPlansModalOpen(true)}
              sx={{ textTransform: "none", color: "var(--wb-blue)" }}
            >
              Ver planes
            </Button>
          </Box>
        </Box>

        {/* Número de teléfono con verificación */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="gray">
            Número de Teléfono
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1">{resolvedPhone || "No disponible"}</Typography>
            {telefonoVerificado ? (
              <span style={{ color: "limegreen" }}>✔️</span>
            ) : (
              <span style={{ color: "red" }}>❌</span>
            )}
          </Box>

          {!telefonoVerificado && (
            <>
              <Button
                variant="outlined"
                sx={{ mt: 1, borderRadius: 2, textTransform: "none" }}
                onClick={handleSendCode}
              >
                Enviar código SMS
              </Button>

              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <input
                  type="text"
                  placeholder="Código"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid var(--wb-x-border-555555, #555)",
                    background: "var(--wb-x-bg-1c1f26, #1c1f26)",
                    color: "var(--wb-text-strong)",
                  }}
                />
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleVerifyCode}
                >
                  Verificar
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Sección: Seguridad */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Lock sx={{ color: "gray", mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Seguridad
          </Typography>
        </Box>

        <Typography variant="body2" color="gray" sx={{ mb: 2 }}>
          Cambia tu contraseña para mantener tu cuenta segura
        </Typography>

        <ChangePasswordForm />
      </Box>

      {/* Sección: Verificación en dos pasos (MFA/TOTP) */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Lock sx={{ color: "gray", mr: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Verificación en dos pasos
          </Typography>
        </Box>

        {mfaLoading && (
          <Typography variant="body2" color="gray">
            Consultando estado...
          </Typography>
        )}

        {!mfaLoading && mfaEnabled && (
          <Typography variant="body2" sx={{ color: "limegreen" }}>
            ✔️ Activa con app autenticadora
          </Typography>
        )}

        {!mfaLoading && !mfaEnabled && (
          <TotpEnrollmentForm onEnrolled={() => setMfaEnabled(true)} />
        )}
      </Box>

      <PlanComparisonModal
        open={plansModalOpen}
        onClose={() => setPlansModalOpen(false)}
        accountId={resolvedAccountId}
        isTenantAdmin={isTenantAdmin}
        isPlatformRoot={isPlatformRoot}
        currentPlanId={planInfo?.plan_id}
        hasActiveSubscription={Boolean(planInfo?.stripe_subscription_id)}
        onPlanChanged={() => {
          if (!resolvedAccountId) return;
          CreditsEngineService.getLimits({ accountId: resolvedAccountId })
            .then((response) => setPlanInfo(response?.account_policy || null))
            .catch((err) => console.error('No se pudo refrescar la información del plan', err));
        }}
      />
    </Drawer>
  );
};

export default ProfileDrawer;
